/**
 * One section of the rail: a heading you can press, and what is under it.
 *
 * The heading is `.crm-msect` grown a chevron - deliberately the same 10px
 * letter-spaced capitals the rail has always used to separate its groups. The
 * sections were there before this; the only new thing is that they close.
 *
 * A CLOSED SECTION STILL HAS TO SAY WHAT IS IN IT. That is what `summary` is
 * for, and it is the whole reason this is worth building rather than hiding
 * four groups behind four words. The folder you are looking at, the label you
 * are filtered on, whether the telephone rings here and where it is being
 * forwarded to: every one of those is a fact somebody acts on, and folding it
 * out of sight would trade a tidy rail for an operator who does not know which
 * calls they are looking at. The summary is drawn only while the section is
 * shut, because open it would be the same fact printed twice.
 *
 * The body is unmounted rather than hidden, and one section depends on that:
 * the audio panel opens a microphone while it is on screen (see `RailAudio`).
 * `hidden` would leave it metering behind a collapsed heading forever.
 */
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@wa/ui";

export function RailFold({
  title,
  open,
  onToggle,
  summary,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  /** The one line the section is worth while it is shut. */
  summary?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={cn("crm-acc", open && "is-open")}>
      <button type="button" className="crm-acc-h" aria-expanded={open} onClick={onToggle}>
        <span className="crm-acc-t">{title}</span>
        {open ? null : summary}
        <ChevronDown className="crm-acc-chev size-3.5" strokeWidth={2.5} />
      </button>
      {open ? <div className="crm-acc-b">{children}</div> : null}
    </section>
  );
}
